/**
 * ClevertapIntegrationPage — CleverTap server-to-server config registration.
 * Form fields: external_id, account_id, passcode, region. Lists existing configs below.
 */
import { useState, type FormEvent } from "react";
import {
	useClevertapIntegrationCreate,
	useClevertapIntegrationList,
} from "./hooks";
import { CLEVERTAP_REGIONS } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";

function formatDate(ts: number): string {
	try {
 return new Date(ts * 1000).toISOString().replace("T","").slice(0, 19);
	} catch {
 return String(ts);
	}
}

export function ClevertapIntegrationPage() {
	const listQ = useClevertapIntegrationList();
	const createMut = useClevertapIntegrationCreate();
	const [externalId, setExternalId] = useState("");
	const [accountId, setAccountId] = useState("");
	const [passcode, setPasscode] = useState("");
	const [region, setRegion] = useState<string>("us1");
	const [saved, setSaved] = useState(false);

	const handleSubmit = (e: FormEvent) => {
 e.preventDefault();
 setSaved(false);
 createMut.mutate(
 {
 external_id: externalId.trim(),
 account_id: accountId.trim(),
 passcode: passcode.trim(),
 region,
 },
 {
 onSuccess: () => {
 setSaved(true);
 setExternalId("");
 setAccountId("");
 setPasscode("");
 setRegion("us1");
 },
 },
 );
	};

	const items = listQ.data?.items ?? [];
	const isLoading = listQ.isLoading;
	const isEmpty = !isLoading && items.length === 0;

	return (
 <main
 data-testid="clevertap-integration-page"
 className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <h1 className="text-3xl font-semibold text-green-700">
 CleverTap Integration
 </h1>
 <p className="text-sm text-zinc-600">
 Register a CleverTap account for server-to-server event forwarding. Stored creds stay scoped to your tenant.
 </p>
 </header>

 <form
 onSubmit={handleSubmit}
 className={`${CARD} flex flex-col gap-4`}
 data-testid="clevertap-integration-form"
 aria-label="clevertap-integration form"
 >
 <div className="flex flex-col gap-1.5">
 <label htmlFor="clevertap-integration-external-id" className={LABEL}>
 External ID
 </label>
 <input
 id="clevertap-integration-external-id"
 type="text"
 required
 maxLength={128}
 className={INPUT}
 value={externalId}
 onChange={(e) => setExternalId(e.currentTarget.value)}
 data-testid="clevertap-integration-external-id-input"
 placeholder="e.g. prod-main"
 />
 <p className="text-xs text-zinc-500">Unique label you pick to identify this config.</p>
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="clevertap-integration-account-id" className={LABEL}>
 CleverTap Account ID
 </label>
 <input
 id="clevertap-integration-account-id"
 type="text"
 required
 maxLength={128}
 className={INPUT}
 value={accountId}
 onChange={(e) => setAccountId(e.currentTarget.value)}
 data-testid="clevertap-integration-account-id-input"
 />
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="clevertap-integration-passcode" className={LABEL}>
 CleverTap Passcode
 </label>
 <input
 id="clevertap-integration-passcode"
 type="password"
 required
 maxLength={128}
 className={INPUT}
 value={passcode}
 onChange={(e) => setPasscode(e.currentTarget.value)}
 data-testid="clevertap-integration-passcode-input"
 autoComplete="new-password"
 />
 <p className="text-xs text-zinc-500">Stored server-side, never echoed back.</p>
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="clevertap-integration-region" className={LABEL}>
 Region
 </label>
 <select
 id="clevertap-integration-region"
 className={INPUT}
 value={region}
 onChange={(e) => setRegion(e.currentTarget.value)}
 data-testid="clevertap-integration-region-input"
 >
 {CLEVERTAP_REGIONS.map((r) => (
 <option key={r.value} value={r.value}>
 {r.label}
 </option>
 ))}
 </select>
 </div>

 <div className="flex items-center gap-3">
 <button
 type="submit"
 className={BTN}
 disabled={createMut.isPending}
 data-testid="clevertap-integration-save"
 >
 {createMut.isPending ? "Saving." : "Save config"}
 </button>
 {saved ? (
 <p className="text-sm text-green-700" role="status">
 Saved.
 </p>
 ) : null}
 {createMut.error ? (
 <p className="text-sm text-red-600" role="alert">
 {createMut.error.message}
 </p>
 ) : null}
 </div>
 </form>

 <section className={`${CARD} flex flex-col gap-3`} aria-label="existing configs">
 <h2 className="text-green-700 font-semibold text-lg font-semibold text-zinc-900">Registered configs</h2>
 {isLoading ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="clevertap-integration-loading"
 >
 Loading configs.
 </p>
 ) : null}
 {isEmpty ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="clevertap-integration-empty"
 >
 No configs yet. Save your first one above.
 </p>
 ) : null}
 {items.length > 0 ? (
 <ul className="flex flex-col divide-y divide-zinc-200" data-testid="clevertap-integration-list">
 {items.map((row) => (
 <li
 key={row.id}
 className="flex flex-col gap-1 py-3"
 data-testid="clevertap-integration-row"
 >
 <div className="flex items-center justify-between gap-3">
 <span className="text-sm font-medium text-zinc-900">
 {row.external_id}
 </span>
 <span className="rounded-[4px] border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
 {row.region}
 </span>
 </div>
 <div className="flex items-center justify-between gap-3 text-xs text-zinc-500">
 <span>Account: {row.account_id}</span>
 <span>{formatDate(row.created_at)}</span>
 </div>
 </li>
 ))}
 </ul>
 ) : null}
 {listQ.error ? (
 <p className="text-sm text-red-600" role="alert">
 {listQ.error.message}
 </p>
 ) : null}
 </section>
 </main>
	);
}

export default ClevertapIntegrationPage;
